import { RuleId } from '@wix/a11y-rules';
import { useExperiments } from '@wix/fe-essentials/react';
import { RuleListStatus } from '@wix/rules-panel-ui';
import { assertNotOptional } from '@wix/seo-common-core';
import { omit, pick } from 'lodash';
import { useCallback } from 'react';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useColorContrastRule, useHeadingStructureRule } from '../../rule-mappers/page-level-rules';
import { useAltTextRule } from '../../rule-mappers/page-level-rules/use-alt-text-rule';
import { useInaccessibleComponentsRule } from '../../rule-mappers/page-level-rules/use-inaccessible-components-rule';
import { useInaccessibleLayoutRule } from '../../rule-mappers/page-level-rules/use-inaccessible-layout-rule';
import { useMediaAlternativesRule } from '../../rule-mappers/page-level-rules/use-media-alternatives-rule';
import { useGlobalSectionState } from '../../rule-mappers/shared/use-global-section-state';
import { getCrossColorContrastIssuesByBreakpoint } from '../verticals-drill-in/get-cross-color-contrast-issues';
import { getCrossHeadingStructureIssuesByBreakpoint } from '../verticals-drill-in/get-cross-heading-structure-issues/get-cross-heading-structure-issues';
export const useBuildCrossPagesRuleResult = () => {
  const {
    getColorContrast
  } = useColorContrastRule();
  const {
    getAltTextRule
  } = useAltTextRule();
  const {
    getMediaAlternatives
  } = useMediaAlternativesRule();
  const {
    getHeadingStructure
  } = useHeadingStructureRule();
  const {
    getInaccessibleComponents
  } = useInaccessibleComponentsRule();
  const {
    getInaccessibleLayout
  } = useInaccessibleLayoutRule();
  const {
    getIsComponentInGlobalSection
  } = useGlobalSectionState();
  const {
    getPagesData
  } = usePanelStateAtom();
  const {
    experiments
  } = useExperiments();
  const shouldSkipAltTextAndMediaAltRules = experiments.enabled('specs.a11y.disableAltTextAndMediaAltForTemplatesQA');
  return useCallback(_ref => {
    var _getPagesData, _crossPagesColorContr, _crossPagesHeadingStr, _executionResults$pag, _executionResults$pag2;
    let {
      executionResults,
      pageId
    } = _ref;
    assertNotOptional(pageId, 'pageId');
    const innerRoutes = (_getPagesData = getPagesData()) == null || (_getPagesData = _getPagesData.get(pageId)) == null ? void 0 : _getPagesData.innerRoutes;
    const innerRoutesIds = innerRoutes == null ? void 0 : innerRoutes.map(route => route.id);
    const ruleResultGroupByInnerRoute = innerRoutesIds != null && innerRoutesIds.length ? pick(executionResults, innerRoutesIds) : omit(executionResults, pageId);
    const crossPagesColorContrastIssues = getCrossColorContrastIssuesByBreakpoint(getIsComponentInGlobalSection, ruleResultGroupByInnerRoute);
    const crossPagesHeadingStructureIssues = getCrossHeadingStructureIssuesByBreakpoint(ruleResultGroupByInnerRoute);
    const altTextRuleResult = Object.values(executionResults).flatMap(ruleDict => ruleDict[RuleId.STATIC_ALT_TEXT] ?? []);
    const mediaAlternativesRuleResult = Object.values(executionResults).flatMap(ruleDict => ruleDict[RuleId.MEDIA_ALTERNATIVES] ?? []);
    const colorContrastRuleResult = crossPagesColorContrastIssues === null || crossPagesColorContrastIssues.DESKTOP === null ? null : getColorContrast({
      isCrossPage: true,
      pageId,
      routerId: (_crossPagesColorContr = crossPagesColorContrastIssues.DESKTOP.metadata) == null ? void 0 : _crossPagesColorContr.pageId,
      ruleExecutionResult: crossPagesColorContrastIssues.DESKTOP
    });
    const colorContrastRuleResultToRender = (colorContrastRuleResult == null ? void 0 : colorContrastRuleResult.status) === RuleListStatus.passed && colorContrastRuleResult.elementCount === 0 ? null : colorContrastRuleResult;
    const headingStructureRule = crossPagesHeadingStructureIssues === null || crossPagesHeadingStructureIssues.DESKTOP === null ? null : getHeadingStructure({
      isCrossPage: true,
      pageId,
      routerId: (_crossPagesHeadingStr = crossPagesHeadingStructureIssues.DESKTOP.metadata) == null ? void 0 : _crossPagesHeadingStr.pageId,
      ruleExecutionResult: crossPagesHeadingStructureIssues.DESKTOP
    });
    const inaccessibleComponentsResult = ((_executionResults$pag = executionResults[pageId]) == null ? void 0 : _executionResults$pag[RuleId.INACCESSIBLE_COMPONENTS]) ?? [];
    const inaccessibleLayoutResult = ((_executionResults$pag2 = executionResults[pageId]) == null ? void 0 : _executionResults$pag2[RuleId.INACCESSIBLE_LAYOUT]) ?? [];
    const ruleResult = [getInaccessibleComponents({
      isCrossPage: true,
      pageId,
      ruleExecutionResult: inaccessibleComponentsResult
    }), getInaccessibleLayout({
      isCrossPage: true,
      pageId,
      ruleExecutionResult: inaccessibleLayoutResult
    }), headingStructureRule, shouldSkipAltTextAndMediaAltRules ? null : getAltTextRule({
      isCrossPage: true,
      pageId,
      ruleExecutionResult: altTextRuleResult
    }), colorContrastRuleResultToRender, shouldSkipAltTextAndMediaAltRules ? null : getMediaAlternatives({
      isCrossPage: true,
      pageId,
      ruleExecutionResult: mediaAlternativesRuleResult
    })].filter(result => result !== null);
    return {
      crossPagesIssues: {
        crossPagesColorContrastIssues,
        crossPagesHeadingStructureIssues
      },
      crossPagesRuleResult: ruleResult,
      pageId
    };
  }, [getPagesData, getIsComponentInGlobalSection, shouldSkipAltTextAndMediaAltRules, getColorContrast, getHeadingStructure, getInaccessibleComponents, getInaccessibleLayout, getAltTextRule, getMediaAlternatives]);
};
//# sourceMappingURL=use-build-cross-pages-rule-result.js.map